/**
 * The proposal list.
 *
 * Scoped server-side: a manager sees their own departments and nothing else, and asking for one
 * they cannot see narrows to nothing rather than erroring — a filter is not an authorisation
 * attempt. So this screen can offer the filters without guarding them.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useSession } from '@/hooks/session';
import { Deadline, Money, PageHeader, ProposalStatusBadge, RefLink } from '@/components/bits';
import { Badge, Card, EmptyState, LinkButton, Select, Table, Td, Th } from '@/components/ui';
import { PROPOSAL_STATUS, activityLabel } from '@/lib/policy';
import { formatDate } from '@/lib/dates';
import type { ProposalStatus } from '@/lib/types';

const GROUPS: Array<{ value: string; label: string; statuses?: ProposalStatus[] }> = [
  { value: 'all', label: 'All' },
  {
    value: 'open',
    label: 'In progress',
    statuses: ['draft', 'sponsor_review', 'hr_review', 'clarification_requested', 'returned'],
  },
  { value: 'confirmed', label: 'Confirmed', statuses: ['confirmed', 'claim_due', 'claim_overdue'] },
  { value: 'closed', label: 'Closed', statuses: ['claimed', 'rejected', 'cancelled', 'expired'] },
];

export function Proposals() {
  const session = useSession();
  const [group, setGroup] = useState('all');

  const chosen = GROUPS.find((entry) => entry.value === group);
  const proposals = useQuery({
    queryKey: ['proposals', session.year, group],
    queryFn: () =>
      api.proposals({
        budget_year: session.year,
        ...(chosen?.statuses ? { status: chosen.statuses } : {}),
        limit: 200,
      }),
  });

  const items = proposals.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Proposals"
        subtitle={`${session.year} · ${proposals.data?.total ?? 0} in total`}
        actions={
          session.me.capabilities.raise_proposal ? (
            <LinkButton to="/proposals/new" variant="primary">
              Propose an event
            </LinkButton>
          ) : null
        }
      />

      <Card
        title="Events"
        action={
          <Select
            aria-label="Filter by stage"
            className="h-8 w-40 py-1"
            value={group}
            onChange={(event) => setGroup(event.target.value)}
          >
            {GROUPS.map((entry) => (
              <option key={entry.value} value={entry.value}>
                {entry.label}
              </option>
            ))}
          </Select>
        }
      >
        {proposals.isPending ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : items.length === 0 ? (
          <EmptyState title="Nothing here yet">
            Proposals you raise, and any waiting on you, appear in this list.
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Reference</Th>
                <Th>Event</Th>
                <Th>Department</Th>
                <Th>Date</Th>
                <Th numeric>People</Th>
                <Th numeric>Pool</Th>
                <Th numeric>Approved</Th>
                <Th>Claim by</Th>
                <Th>Stage</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <Td>
                    <RefLink to={`/proposals/${item.id}`} refNo={item.ref_no} />
                  </Td>
                  <Td>
                    {item.activity_title}
                    <p className="text-muted-foreground text-xs">
                      {activityLabel(item.activity_type)}
                    </p>
                  </Td>
                  <Td className="text-xs">{item.department_name}</Td>
                  <Td className="whitespace-nowrap">{formatDate(item.proposed_date)}</Td>
                  <Td numeric>
                    {item.attendee_count ?? '—'}
                    {item.participation_pct === null ? null : (
                      <span className="text-muted-foreground ml-1 text-xs">
                        {Math.round(item.participation_pct)}%
                      </span>
                    )}
                  </Td>
                  <Td numeric>
                    <Money cents={item.computed_pool_cents} muted />
                  </Td>
                  <Td numeric>
                    <Money cents={item.approved_amount_cents} />
                  </Td>
                  <Td>
                    <Deadline date={item.claim_deadline} />
                  </Td>
                  <Td>
                    <div className="flex flex-col items-start gap-1">
                      <ProposalStatusBadge status={item.status} />
                      {item.pending_exceptions > 0 ? (
                        <Badge tone="warn">
                          {item.pending_exceptions} pending{' '}
                          {item.pending_exceptions === 1 ? 'exception' : 'exceptions'}
                        </Badge>
                      ) : null}
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <p className="text-muted-foreground mt-4 text-xs">{PROPOSAL_STATUS.confirmed.hint}</p>
    </>
  );
}
